import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import SettingsModal from "@/components/SettingsModal";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import ManualOverview from "@/pages/ManualOverview";
import TeachingKit from "@/pages/TeachingKit";
import StepViewer from "@/pages/StepViewer";
import Completion from "@/pages/Completion";
import ShopSupplies from "@/pages/ShopSupplies";
import BuyLater from "@/pages/BuyLater";
import Community from "@/pages/Community";
import CommunityManual from "@/pages/CommunityManual";
import PathCommunity from "@/pages/PathCommunity";
import CreatorPage from "@/pages/CreatorPage";
import History from "@/pages/History";
import MyVideos from "@/pages/MyVideos";
// SHELVED — Podcasts moved to the holding bin (with MovieStar); pages kept intact for later.
// import MyPodcasts from "@/pages/MyPodcasts";
// import PodcastEditor from "@/pages/PodcastEditor";
import FAQ from "@/pages/FAQ";
import Wallet from "@/pages/Wallet";
import Pricing from "@/pages/Pricing";
import Moderation from "@/pages/Moderation";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import DIY from "@/pages/DIY";
import MinuteMode from "@/pages/MinuteMode";
// SHELVED — Podcast creator page kept in the holding bin for later.
// import Podcast from "@/pages/Podcast";
import Seller from "@/pages/Seller";
import Analytics from "@/pages/Analytics";
import DisclaimerGate from "@/components/DisclaimerGate";
import AdminGate from "@/components/AdminGate";
import TrafficTracker from "@/components/TrafficTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

function AppLayout() {
  const { user, loading } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={30} className="spin text-[#5d8a52]" />
      </div>
    );
  }
  // Logged-in area. Any account may enter (DIY, their manuals, videos, pricing).
  // The AI creation Studio is further gated to admins via <AdminOnly> on its route.
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen">
      <Header onOpenSettings={() => setSettingsOpen(true)} />
      <Outlet context={{ openSettings: () => setSettingsOpen(true) }} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <DisclaimerGate />
    </div>
  );
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  // The AI creation studio opens once you have funds — admins (unlimited) always, or any user with a
  // positive balance. No funds → sent to Plans to add credits first.
  if (!user) return <Navigate to="/" replace />;
  if (!user.unlimited && (user.credits ?? 0) <= 0) return <Navigate to="/pricing" replace />;
  return children;
}

function PublicLayout() {
  const { loading } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={30} className="spin text-[#5d8a52]" />
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      <Header onOpenSettings={() => setSettingsOpen(true)} />
      <Outlet />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <AdminGate />
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/community" element={<Navigate to="/" replace />} />
      <Route element={<PublicLayout />}>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route index element={<Community />} />
        {/* SHELVED podcasts feed — holding bin */}
        {/* <Route path="/podcasts" element={<Community mode="podcasts" />} /> */}
        <Route path="/community/:id" element={<CommunityManual />} />
        <Route path="/path/:topic" element={<PathCommunity />} />
        <Route path="/creator/:username" element={<CreatorPage />} />
        <Route path="/moderation" element={<Moderation />} />
      </Route>
      <Route element={<AppLayout />}>
        <Route path="/studio" element={<AdminOnly><Home /></AdminOnly>} />
        <Route path="/diy" element={<DIY />} />
        <Route path="/minute" element={<MinuteMode />} />
        <Route path="/minute/:id" element={<MinuteMode />} />
        {/* SHELVED podcast creator/editor/my-podcasts — holding bin (files intact) */}
        {/* <Route path="/podcast" element={<Podcast />} /> */}
        <Route path="/my-videos" element={<MyVideos />} />
        {/* <Route path="/my-podcasts" element={<MyPodcasts />} /> */}
        {/* <Route path="/podcast-edit/:id" element={<PodcastEditor />} /> */}
        <Route path="/faq" element={<FAQ />} />
        <Route path="/analytics" element={<Analytics />} />
        {/* <Route path="/podcast-analytics" element={<Analytics content="podcast" />} /> */}
        <Route path="/history" element={<History />} />
        <Route path="/buy-later" element={<BuyLater />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/seller" element={<Seller />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/manual/:id" element={<ManualOverview />} />
        <Route path="/manual/:id/step/:index" element={<StepViewer />} />
        <Route path="/manual/:id/complete" element={<Completion />} />
        <Route path="/manual/:id/kit" element={<TeachingKit />} />
        <Route path="/manual/:id/shop" element={<ShopSupplies />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <TrafficTracker />
          <AppRouter />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
